import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import Navbar from './components/Layout/Navbar';
import Sidebar from './components/Layout/Sidebar';
import Dashboard from './pages/Dashboard';
import WorkOrders from './pages/WorkOrders';
import BatchExecution from './pages/BatchExecution';
import QAReview from './pages/QAReview';
import BatchSearch from './pages/BatchSearch';
import AuditTrail from './pages/AuditTrail';
import MBREditor from './pages/MBREditor';
import BatchMonitor from './pages/BatchMonitor';
import WmcInventory from './pages/WmcInventory';
import WmcSuppliers from './pages/WmcSuppliers';
import WmcReceiving from './pages/WmcReceiving';
import WmcQuarantine from './pages/WmcQuarantine';
import WmcInspections from './pages/WmcInspections';
import WmcPurchaseOrders from './pages/WmcPurchaseOrders';

// Dark theme configuration
const theme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#0F0F0F',
      paper: '#1F2937',
    },
    primary: {
      main: '#3B82F6',
    },
    secondary: {
      main: '#6B7280',
    },
    error: {
      main: '#EF4444',
    },
    warning: {
      main: '#F59E0B',
    },
    success: {
      main: '#10B981',
    },
    text: {
      primary: '#F9FAFB',
      secondary: '#D1D5DB',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 500,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#1F2937',
          border: '1px solid #374151',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1F2937',
          borderBottom: '1px solid #374151',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
});

// Status color mapping
export const statusColors = {
  pending: '#F59E0B',
  in_progress: '#3B82F6',
  completed: '#10B981',
  approved: '#22C55E',
  rejected: '#EF4444',
  paused: '#6B7280',
  cancelled: '#EF4444',
  failed: '#EF4444',
  skipped: '#9CA3AF',
};

function App() {
  const [sidebarOpen, setSidebarOpen] = React.useState(false); // false = collapsed (icons), true = expanded (icons+labels)

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
          <Navbar onMenuClick={toggleSidebar} />
          <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              ml: { xs: 0, md: sidebarOpen ? '200px' : '50px' },
              mt: { xs: '56px', md: '64px' },
              p: { xs: 1, md: 0.5 },
              transition: 'margin-left 0.3s',
              backgroundColor: 'background.default',
              minHeight: 'calc(100vh - 64px)',
            }}
          >
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/batch-monitor" element={<BatchMonitor />} />
              <Route path="/work-orders" element={<WorkOrders />} />
              <Route path="/batch-execution/:batchId" element={<BatchExecution />} />
              <Route path="/qa-review" element={<QAReview />} />
              <Route path="/batch-search" element={<BatchSearch />} />
              <Route path="/audit-trail" element={<AuditTrail />} />
              <Route path="/mbr-editor" element={<MBREditor />} />
              <Route path="/wmc/inventory" element={<WmcInventory />} />
              <Route path="/wmc/suppliers" element={<WmcSuppliers />} />
              <Route path="/wmc/receiving" element={<WmcReceiving />} />
              <Route path="/wmc/quarantine" element={<WmcQuarantine />} />
              <Route path="/wmc/inspections" element={<WmcInspections />} />
              <Route path="/wmc/purchase-orders" element={<WmcPurchaseOrders />} />
            </Routes>
          </Box>
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;